import { motion } from "framer-motion";
import { PATHWAYS } from "@/data/journeys";
import { PLACEMENTS } from "@/data/interpretations";
import { Overline } from "@/components/Brand";

const Notes = ({ notes }) =>
  notes?.length ? (
    <div className="space-y-2 mt-4">
      {notes.map((n) => <p key={n} className="text-sm text-[#9A5547] italic border-l-2 border-[#C9A24A]/50 pl-3">{n}</p>)}
    </div>
  ) : null;

const PathwayStep = ({ pid, i, showPractices, testid, first }) => {
  const p = PATHWAYS[pid];
  return (
    <li data-testid={testid} className="relative pl-12 pb-6 last:pb-0">
      <span className="absolute left-0 top-0 grid place-items-center w-8 h-8 rounded-full border border-[#C9A24A]/60 bg-[#FFFCF6] font-serif-display text-[#B76E60]">{i + 1}</span>
      <span className="absolute left-4 top-8 bottom-0 w-px bg-[#C9A24A]/30" />
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-serif-display text-lg sm:text-xl text-[#4A2A20]">{p.name}</h4>
        {first && <span className="text-[10px] tracking-[0.18em] uppercase rounded-full px-2.5 py-1 bg-[#B76E60] text-white">Start here</span>}
      </div>
      <p className="text-sm text-[#7D5A4B] mt-1 leading-relaxed">{p.short}</p>
      {showPractices && (
        <ul className="mt-3 space-y-1.5">
          {p.practices.slice(0, 2).map((pr) => <li key={pr} className="text-sm text-[#4A2A20] flex gap-2"><span className="text-[#C9A24A]">✦</span>{pr}</li>)}
        </ul>
      )}
    </li>
  );
};

export const PlacementCard = ({ pl, index }) => {
  const meta = PLACEMENTS[pl.key];
  const j = pl.journey;
  const reverse = index % 2 === 1;
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.7 }}
      data-testid={`placement-${pl.key}`}
      className={`rounded-[2rem] overflow-hidden border border-[#C9A24A]/30 bg-white/75 grid ${reverse ? "lg:grid-cols-[1fr_380px]" : "lg:grid-cols-[380px_1fr]"}`}
    >
      <div className={`relative ${reverse ? "lg:order-2" : ""}`} style={{ background: j.tint }}>
        <img src={j.image} alt={j.name} data-testid={`placement-${pl.key}-image`} className="w-full aspect-[4/5] object-cover lg:sticky lg:top-6" />
      </div>
      <div className="p-6 sm:p-10" style={{ background: `linear-gradient(160deg, ${j.tint}, #FFFCF6 60%)` }}>
        <div className="font-serif-display text-2xl sm:text-3xl text-[#B76E60] mb-2" data-testid={`placement-${pl.key}-tag`}>{meta.tag}</div>
        <Overline>{meta.heading}</Overline>
        <h3 className="font-serif-display text-3xl sm:text-4xl mt-3" style={{ color: j.accent }} data-testid={`placement-${pl.key}-journey-name`}>{j.name}</h3>
        <p className="text-xs text-[#B8892F] mt-2">Current resonance · {pl.score}%</p>
        <p className="text-[15px] text-[#4A2A20] leading-relaxed mt-5">{pl.explanation}</p>
        {pl.highlight && <p className="text-[15px] text-[#4A2A20] leading-relaxed mt-3">{pl.highlight}</p>}
        <Notes notes={pl.flagNotes} />
        <PlacementDetail pl={pl} />
      </div>
    </motion.section>
  );
};

const Block = ({ title, children }) => (
  <div className="mt-7">
    <div className="font-serif-display text-xl text-[#4A2A20] mb-3">{title}</div>
    {children}
  </div>
);

const PlacementDetail = ({ pl }) => {
  const seqTitle = { primary: "Your recommended pathway sequence", secondary: "Your recommended pathway sequence", interwoven: "Supportive pathways to weave in", later: "Pathways to consider when you return" }[pl.key];
  return (
    <>
      {pl.key === "primary" && <Block title="Why this is your starting place"><p className="text-sm text-[#7D5A4B] leading-relaxed">{pl.why}</p></Block>}
      {pl.key === "secondary" && (
        <Block title="How it builds upon your primary journey">
          <p className="text-sm text-[#7D5A4B] leading-relaxed">{pl.builds}</p>
          {pl.closeNote && <p className="text-sm text-[#9A5547] italic mt-2" data-testid="close-scores-note">{pl.closeNote}</p>}
        </Block>
      )}
      <Block title={seqTitle}>
        <ol data-testid={`placement-${pl.key}-sequence`}>
          {pl.sequence.map((pid, i) => (
            <PathwayStep key={pid} pid={pid} i={i} first={pl.key === "primary" && i === 0} showPractices={pl.key === "primary" || pl.key === "interwoven"} testid={`placement-${pl.key}-pathway-${pid}`} />
          ))}
        </ol>
      </Block>
      {pl.key === "interwoven" && (
        <Block title="Ways to weave it alongside your primary journey">
          <ul className="space-y-2">{pl.tips.map((t) => <li key={t} className="text-sm text-[#4A2A20] flex gap-2"><span className="text-[#C9A24A]">✦</span>{t}</li>)}</ul>
        </Block>
      )}
      {pl.key === "later" && <Block title="Why later"><p className="text-sm text-[#7D5A4B] leading-relaxed" data-testid="later-why">{pl.why}</p></Block>}
      {pl.key !== "later" && pl.optional?.length > 0 && (
        <Block title="Optional future exploration">
          <div className="flex flex-wrap gap-2">
            {pl.optional.map((pid) => <span key={pid} className="text-xs rounded-full px-3 py-1.5 border border-[#C9A24A]/40 text-[#7D5A4B] bg-white/70">{PATHWAYS[pid].name}</span>)}
          </div>
        </Block>
      )}
    </>
  );
};
